import { Link } from 'react-router-dom'
import type { ColumnDef } from '@tanstack/react-table'
import { useAllRooms, useBuildings } from '@/hooks'
import { useOrg } from '@/context/OrgContext'
import { PageHeader, DataTable, SortableHeader } from '@/components'
import type { Room } from '@/types'

export function RoomsPage() {
  const { effectiveOrgId } = useOrg()
  const { data: rooms, isLoading, error } = useAllRooms()
  const { data: buildings } = useBuildings(effectiveOrgId)

  // Super admin with no org selected
  if (!effectiveOrgId) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Rooms"
          description="All rooms across your organization. Manage rooms from their building's detail page."
        />
        <div className="text-center py-12">
          <p className="text-lg font-semibold mb-2">No Organization Selected</p>
          <p className="text-muted-foreground">
            Select an organization using the switcher in the header to view rooms.
          </p>
        </div>
      </div>
    )
  }

  // Create a map of building ID to building name for display
  const buildingMap = new Map(
    buildings?.map((b) => [b.id, b.name]) ?? []
  )

  const columns: ColumnDef<Room>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <SortableHeader column={column}>Name</SortableHeader>
      ),
      cell: ({ row }) => (
        <Link
          to={`/rooms/${row.original.id}`}
          className="font-medium text-primary hover:underline"
        >
          {row.getValue('name')}
        </Link>
      ),
    },
    {
      accessorKey: 'building_id',
      header: ({ column }) => (
        <SortableHeader column={column}>Building</SortableHeader>
      ),
      cell: ({ row }) => {
        const buildingId = row.getValue('building_id') as string
        const buildingName = buildingMap.get(buildingId)
        return buildingName ? (
          <Link
            to={`/buildings/${buildingId}`}
            className="text-primary hover:underline"
          >
            {buildingName}
          </Link>
        ) : (
          <span className="text-muted-foreground">Unknown</span>
        )
      },
    },
    {
      accessorKey: 'floor',
      header: ({ column }) => (
        <SortableHeader column={column}>Floor</SortableHeader>
      ),
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {row.getValue('floor') || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => (
        <SortableHeader column={column}>Created</SortableHeader>
      ),
      cell: ({ row }) => {
        const date = new Date(row.getValue('created_at'))
        return date.toLocaleDateString()
      },
    },
  ]

  if (error) {
    return (
      <div className="text-destructive">
        Failed to load rooms: {error.message}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Rooms"
        description="All rooms across your organization. Manage rooms from their building's detail page."
      />

      {isLoading ? (
        <div className="text-muted-foreground">Loading rooms...</div>
      ) : rooms && rooms.length > 0 ? (
        <DataTable
          columns={columns}
          data={rooms}
          filterColumn="name"
          filterPlaceholder="Filter by name..."
        />
      ) : (
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            No rooms found. Add rooms from a building's detail page.
          </p>
        </div>
      )}
    </div>
  )
}
